import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import * as Sentry from "@sentry/react";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Detects media type from a URL by checking the file extension.
 * 
 * @param url - The URL to check
 * @returns 'image' or 'video' based on file extension, defaults to 'image'
 */
export const detectMediaType = (url: string): 'image' | 'video' => {
  const urlLower = url.toLowerCase();
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
  const hasVideoExtension = videoExtensions.some(ext => urlLower.includes(ext));
  return hasVideoExtension ? 'video' : 'image';
};

/**
 * Detects media type from a File object by checking the file type MIME string.
 * 
 * @param file - The File object to check
 * @returns 'image' or 'video' based on file type, defaults to 'image'
 */
export const detectMediaTypeFromFile = (file: File): 'image' | 'video' => {
  if (file.type.startsWith('video/')) {
    return 'video';
  }
  // Fallback to extension check if MIME type is not available
  return detectMediaType(file.name);
};

/**
 * Safely gets media type from a database record or URL.
 * Handles null, undefined, empty strings, and invalid values.
 * 
 * @param mediaType - The media_type value from database (can be null/undefined/string)
 * @param imageUrl - The image_url to use for fallback detection
 * @returns 'image' or 'video', always returns a valid value
 */
export const getMediaType = (
  mediaType: string | null | undefined,
  imageUrl?: string | null
): 'image' | 'video' => {
  try {
    // Normalize mediaType: trim whitespace, convert to lowercase
    const normalizedType = mediaType?.trim().toLowerCase();
    
    // Check if it's a valid media type
    if (normalizedType === 'video' || normalizedType === 'image') {
      return normalizedType as 'image' | 'video';
    }
    
    // Fallback to URL detection if available
    if (imageUrl) {
      try {
        return detectMediaType(imageUrl);
      } catch (error) {
        console.warn('[getMediaType] Error detecting media type from URL:', error);
        // Capture to Sentry if available (don't fail if Sentry isn't initialized)
        try {
          Sentry.captureException(error, {
            tags: { errorType: 'media_type_detection' },
            extra: { mediaType, imageUrl },
          });
        } catch {
          // Ignore Sentry errors (e.g., if Sentry isn't initialized)
        }
      }
    }
    
    // Default to 'image' if all else fails
    // Log warning if we had a mediaType but it was invalid
    if (mediaType) {
      console.warn(`[getMediaType] Invalid media_type value: "${mediaType}", defaulting to 'image'`);
    }
    
    return 'image';
  } catch (error) {
    // Catch any unexpected errors and default to 'image'
    console.error('[getMediaType] Unexpected error:', error);
    return 'image';
  }
};
